import { request } from '@/services/apiClient';
import type {
	ActivationToken,
	DeploymentInfo,
	OnpremInstallation,
	OnpremTenant,
	SeatSyncResult,
	SeatSyncState,
} from '../types';

const ONPREM_TENANTS = '/v1/onprem-tenants';

const tenantPath = (tenantId: string) =>
	`${ONPREM_TENANTS}/${encodeURIComponent(tenantId)}`;

/** On-prem network calls. Components never call axios. */
export const onpremApi = {
	/** Public: which kind of deployment the API is. */
	deployment(signal?: AbortSignal): Promise<DeploymentInfo> {
		return request<DeploymentInfo>({
			url: '/v1/deployment',
			method: 'GET',
			signal,
		});
	},

	/* --------------------------------------------------- on-prem install */

	/** Seat sync with VEEP (on-prem admins). */
	syncStatus(signal?: AbortSignal): Promise<SeatSyncState> {
		return request<SeatSyncState>({
			url: '/v1/seats/sync-status',
			method: 'GET',
			signal,
		});
	},

	/** `409 SEAT_SYNC_BUSY` while another sync runs. */
	syncNow(): Promise<SeatSyncResult> {
		return request<SeatSyncResult>({ url: '/v1/seats/sync', method: 'POST' });
	},

	/* ------------------------------------------------- SaaS, platform admin */

	tenants(signal?: AbortSignal): Promise<OnpremTenant[]> {
		return request<OnpremTenant[]>({
			url: ONPREM_TENANTS,
			method: 'GET',
			signal,
		});
	},

	/**
	 * Hosts a new tenant on-prem. `409 ONPREM_TENANT_IN_USE` once it has
	 * displays, seat codes or non-admin users.
	 */
	markOnprem(tenantId: string): Promise<OnpremTenant> {
		return request<OnpremTenant>({ url: tenantPath(tenantId), method: 'PUT' });
	},

	/** A new single-use token; any unused one stops working. */
	issueActivationToken(tenantId: string): Promise<ActivationToken> {
		return request<ActivationToken>({
			url: `${tenantPath(tenantId)}/activation-tokens`,
			method: 'POST',
		});
	},

	revokeInstallation(
		tenantId: string,
		installationId: string,
	): Promise<OnpremInstallation> {
		return request<OnpremInstallation>({
			url: `${tenantPath(tenantId)}/installations/${encodeURIComponent(installationId)}/revoke`,
			method: 'POST',
		});
	},
};
